const LTX_GLOBAL_APP_DEVICE_INFO_LOG_SCHEMA = {
  clustering: {
    fields: ['app_name', 'meta_received_at'],
  },
  name: 'app_device_info_log',
  doc: 'Contains the data of the device. This event sent by any change in these attributes: store_country, usage_events_disabled, platform_user_id, is_subscriber, install_time, receipt_type, lt_id, platform_subscription_id, subscription_tiers',
  namespace: 'com.lightricks.global.analytics',
  subscribers: {
    githubTeamIds: ['Lightricks/data-platform'],
  },
  type: 'record',
  fields: [
    {
      name: 'advertising_id',
      default: null,
      doc: 'A unique identifier to track a user for advertisers. This identifier is limited for iOS 14.5 or higher and will be available only upon acceptance of the user',
      idType: 'AdvertisingId',
      type: ['null', 'string'],
    },
    {
      name: 'app_language',
      default: null,
      doc: 'The language used in the application. Alias to language_used, current_app_language',
      type: ['null', 'string'],
    },
    {
      name: 'app_version_full',
      default: null,
      doc: 'The software version of the event producer that created this event e.g. 2.1.1-Free. Exists only in facetune2 Android',
      type: ['null', 'string'],
    },
    {
      name: 'appsflyer_id',
      default: null,
      doc: 'A unique installation identifier set by AppsFlyer when the app launches for the first time',
      idType: 'AppsflyerId',
      type: ['null', 'string'],
    },
    {
      name: 'cloud_kit_account_status',
      default: null,
      doc: 'Status of the account associated with the CloudKit container. e.g. available, restricted, no_account, temporarily_unavailable. Null in Android',
      type: ['null', 'string'],
    },
    {
      name: 'device_country',
      default: null,
      doc: 'The country according to the device settings (location device)',
      type: ['null', 'string'],
    },
    {
      name: 'device_kind',
      default: null,
      doc: 'Human readable form of the device_model e.g. UIDeviceKindIPhoneX and UIDeviceKindIPadPro12_9. Note that different device_models may have the same value',
      type: ['null', 'string'],
    },
    {
      name: 'device_manufacturer',
      default: null,
      doc: 'The name of the device manufacturer e.g. samsung, huawei',
      type: ['null', 'string'],
    },
    {
      name: 'device_model',
      default: null,
      doc: 'String representation of the device model the app runs on e.g. iPhone10,3, iPad6,7',
      type: ['null', 'string'],
    },
    {
      name: 'device_notifications_token',
      default: null,
      doc: "The current device token assigned to the device by the platform's Push Notifications service. Null if the push notifications are disabled for the device.",
      type: ['null', 'string'],
    },
    {
      name: 'firebase_app_instance_id',
      default: null,
      doc: 'A unique installation identifier set by Firebase when the app launches for the first time. Used in the backend for S2S integrations with Firebase',
      idType: 'FirebaseAppInstanceId',
      type: ['null', 'string'],
    },
    {
      name: 'firmware_id',
      default: null,
      doc: 'Exists only in iOS. Team identifier that signed the app binary, or nil if no signing exists, no team identifier exists (old signing info) or fetching the signing info failed. Note: property name does not resemble real meaning, to obscure from malicious sources',
      type: ['null', 'string'],
    },
    {
      name: 'gpu_renderer',
      default: null,
      doc: 'The type of the GPU renderer, which speed up the rendering process for an image. For Android only',
      type: ['null', 'string'],
    },
    {
      name: 'gpu_vendor',
      default: null,
      doc: 'The vendor of the GPU e.g. Qualcomm, ARM. For Android only',
      type: ['null', 'string'],
    },
    {
      name: 'in_low_power_mode',
      default: null,
      doc: 'True if the device is jailbroken. Nil if the information is unavailable or the instance is of an older version. Note property name does not resemble real meaning, to obscure from malicious sources',
      type: ['null', 'boolean'],
    },
    {
      name: 'install_count',
      doc: 'A counter that identify the install number on the device by the user. Every time a user reinstall the application it will be incremented by 1',
      type: 'long',
    },
    {
      name: 'install_time',
      default: null,
      doc: "The installation time according to platform's receipt. Alias to first_install_time, original_purchase_timestamp in iOS",
      type: [
        'null',
        {
          logicalType: 'timestamp-millis',
          type: 'long',
        },
      ],
    },
    {
      name: 'installer_package_name',
      default: null,
      doc: 'The source of the package which was installed. e.g. google play - com.android.vending. For Android only',
      type: ['null', 'string'],
    },
    {
      name: 'is_advertising_tracking_enabled',
      default: null,
      doc: 'An indication if the user enabled to track advertising, and send advertising_id. Opposite to is_limit_ad_tracking',
      type: ['null', 'boolean'],
    },
    {
      name: 'last_update_time',
      default: null,
      doc: 'The last time that the application had been updated from Google Play. For Android only',
      type: [
        'null',
        {
          logicalType: 'timestamp-millis',
          type: 'long',
        },
      ],
    },
    {
      name: 'modest_definition_revision',
      default: null,
      doc: 'The revision of the Modest experiments definition lastly used in the app, null if modest was never enabled',
      type: ['null', 'long'],
    },
    {
      name: 'network_country',
      default: null,
      doc: 'The country of the app from the mobile device carrier (for both SIM and WIFI). For Android only',
      type: ['null', 'string'],
    },
    {
      name: 'opengl_es_version',
      default: null,
      doc: 'The version of the opengl. For Android only',
      type: ['null', 'string'],
    },
    {
      name: 'os_api_level',
      default: null,
      doc: 'The JAVA api version which is supported by the device. For Android only',
      type: ['null', 'int'],
    },
    {
      name: 'os_version',
      default: null,
      doc: 'The version of the operating system. Alias to ios_version',
      type: ['null', 'string'],
    },
    {
      name: 'receipt_type',
      default: null,
      doc: "An indication of whether it's a production or sandbox user (based on the receipt)",
      type: ['null', 'string'],
    },
    {
      name: 'store_country',
      default: null,
      doc: 'The country of the app store that user is logged to. not always corresponds to the device location or locale. Alias to play_store_country, store_country, app_store_country',
      type: ['null', 'string'],
    },
    {
      name: 'subscription_tiers',
      default: null,
      doc: 'Hydra - Which subscription a user has access to e.g. photo, video (in FT2 iOS)',
      type: ['null', 'string'],
    },
    {
      name: 'targeted_ads_enabled',
      default: null,
      doc: 'An indication whether the user has targeted ads enabled. For Android only',
      type: ['null', 'boolean'],
    },
    {
      name: 'timezone',
      default: null,
      doc: 'A string that represents the timezone which the device is located in',
      type: ['null', 'string'],
    },
    {
      name: 'usage_events_disabled',
      default: null,
      doc: 'An Indication whether the user request that not to send analytics from his device',
      type: ['null', 'boolean'],
    },
    {
      name: 'user_language',
      default: null,
      doc: 'The preferred language of the user on the device. Alias to preferred_language',
      type: ['null', 'string'],
    },

    {
      name: 'app_bundle_id',
      doc: 'Uniquely identifies a single application. bundleIdentifier in iOS. Alias to package_name in Android, bundle_id and current_application_bundle_id',
      type: 'string',
    },
    {
      name: 'app_name',
      doc: 'The internal Lightricks name for the event producer e.g. facetune2, facetune2_android',
      type: 'string',
    },
    {
      name: 'app_version_code',
      doc: 'The software version of the event producer that created this event e.g. 318',
      type: 'string',
    },
    {
      name: 'device_info_id',
      doc: 'Unique identifier corresponding to the device information in the device_info_log table',
      idType: 'DeviceInfoId',
      type: {
        logicalType: 'uuid',
        type: 'string',
      },
    },
    {
      name: 'device_timestamp',
      doc: 'The local wall clock time (in UTC, epoch milliseconds) as seen by the event producer the moment the event was created',
      type: {
        logicalType: 'timestamp-millis',
        type: 'long',
      },
    },
    {
      name: 'editing_session_id',
      default: null,
      doc: 'A unique identifier for editing session. It resets every time the user starts editing a project (or return to edit an existing project). Alias to image_session_id or video_session_id',
      idType: 'EditingSessionId',
      type: ['null', 'string'],
    },
    {
      name: 'event_id',
      doc: 'A unique identifier for the specific event instance',
      type: {
        logicalType: 'uuid',
        type: 'string',
      },
    },
    {
      name: 'first_installation_id',
      default: null,
      doc: 'The first installation_id value that was assigned to the user on that device. Does not change over reinstalls. Note: In Android this fields should be equal to installation_id',
      idType: 'InstallationId',
      type: ['null', 'string'],
    },
    {
      name: 'foreground_count',
      doc: 'A counter that show how many times the app/producer was foregrounded until this event, natural number. The counter is reset once the app is uninstalled (it will start from 1 on the next install)',
      type: 'long',
    },
    {
      name: 'foreground_run_time',
      doc: 'Time spent (in seconds) in foreground since the app started (in epoch ms), at the time the event was created. It resets with run_id. Alias to app_foreground_time',
      type: 'double',
    },
    {
      name: 'installation_id',
      doc: 'An application generated identifier for this installation, changes upon reinstall',
      idType: 'InstallationId',
      type: 'string',
    },
    {
      name: 'is_subscriber',
      doc: 'Indicating whether the user has access to paid content, also true during trial period',
      type: 'boolean',
    },
    {
      name: 'lt_id',
      default: null,
      doc: 'A unique identifier for this specific user, as returned by the Lightricks login API',
      idType: 'LtId',
      type: ['null', 'string'],
    },
    {
      name: 'open_project_id',
      default: null,
      doc: 'The id of the project the user is working on',
      idType: 'OpenProjectId',
      type: ['null', 'string'],
    },
    {
      name: 'platform',
      doc: 'The type of platform the event producer or app is running on e.g. ios, android',
      type: 'string',
    },
    {
      name: 'platform_device_id',
      default: null,
      doc: 'The device issued identifier by the platform for the user. Alias to IDFV in iOS, android_id in Android',
      idType: 'PlatformDeviceId',
      type: ['null', 'string'],
    },
    {
      name: 'platform_subscription_id',
      default: null,
      doc: 'The identifier of the subscription that the user experiences at the time of the event. Alias to purchase token in android, original_transaction_id in iOS',
      idType: 'PlatformSubscriptionId',
      type: ['null', 'string'],
    },
    {
      name: 'platform_user_id',
      default: null,
      doc: 'The platform issued identifier for the user. Alias to icloud_id in iOS, null in Android',
      idType: 'ICloudId',
      type: ['null', 'string'],
    },
    {
      name: 'run_id',
      doc: 'A unique identifier to group all the events sent during the same run of the application. A run is reset every time the app is relaunch (fresh launch)',
      idType: 'RunId',
      type: 'string',
    },
    {
      name: 'session_count',
      doc: 'An incremental counter for the session number. Incremented every time a new session_id is generated. The counter resets to 1 upon install/reinstall',
      type: 'long',
    },
    {
      name: 'session_id',
      doc: 'A unique identifier per app session. A session groups user interaction in a given time frame. The session identifier changes if more than 30 min. passed since the app has been running in the background or relaunch of the app (new run_id). The new identifier will be used the next time the application moves to foreground',
      idType: 'SessionId',
      type: 'string',
    },
    {
      name: 'web_subscription_id',
      default: null,
      doc: 'The identifier of the web subscription that the user experiences at the time of the event. Taken from griffin',
      idType: 'WebSubscriptionId',
      type: ['null', 'string'],
    },
  ],
};

export default LTX_GLOBAL_APP_DEVICE_INFO_LOG_SCHEMA;
